// filepath: /Users/bipul/wspace/ai11_base/ai11_admin/src/pages/Connect/TopicsList.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useGetNotificationTopicsQuery, useDeleteNotificationTopicMutation } from '../../services/connectApi';
import { NotificationTopic } from '../../models';

const TopicsList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<NotificationTopic | null>(null);
  
  // Using Redux hooks for API calls
  const { data, isLoading, isFetching } = useGetNotificationTopicsQuery({
    page: page + 1,
    page_size: rowsPerPage
  });
  
  const [deleteTopic] = useDeleteNotificationTopicMutation();
  
  const topics = data?.results || [];
  const totalCount = data?.count || 0;
  const loading = isLoading || isFetching;
  
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (topic: NotificationTopic) => {
    setTopicToDelete(topic);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (topicToDelete) {
      try {
        await deleteTopic(topicToDelete.id);
      } catch (error) {
        console.error('Error deleting topic:', error);
      } finally {
        setDeleteDialogOpen(false);
        setTopicToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTopicToDelete(null);
  };

  if (loading && topics.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="notification topics table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Firebase Topic</TableCell>
              <TableCell>App</TableCell>
              <TableCell>Public</TableCell>
              <TableCell>Subscribers</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topics.length > 0 ? (
              topics.map((topic) => (
                <TableRow key={topic.id}>
                  <TableCell component="th" scope="row">
                    <Typography variant="body1">{topic.name}</Typography>
                    {topic.description && (
                      <Typography variant="body2" color="textSecondary">
                        {topic.description.length > 50 
                          ? `${topic.description.substring(0, 50)}...` 
                          : topic.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{topic.firebase_topic}</TableCell>
                  <TableCell>{topic.app_name || 'N/A'}</TableCell>
                  <TableCell>
                    {topic.is_public ? (
                      <Chip label="Public" color="success" size="small" />
                    ) : (
                      <Chip label="Private" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{topic.subscriber_count}</TableCell>
                  <TableCell>
                    {new Date(topic.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Send Notification">
                      <IconButton
                        component={Link}
                        to={`/connect/topics/${topic.id}/send`}
                        aria-label="send"
                        size="small"
                      >
                        <NotificationsIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        component={Link}
                        to={`/connect/topics/${topic.id}/edit`}
                        aria-label="edit"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => handleDeleteClick(topic)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body1">No topics found</Typography>
                    <Button 
                      component={Link} 
                      to="/connect/topics/new" 
                      variant="outlined" 
                      sx={{ mt: 2 }}
                    >
                      Create a topic
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <ConfirmDialog
        title="Delete Topic"
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        content={
          topicToDelete
            ? `Are you sure you want to delete the topic "${topicToDelete.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this topic?'
        }
      />
    </>
  );
};

export default TopicsList;
